import { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Nav, Tab, Form, ButtonGroup, Button } from 'react-bootstrap';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import ReminderCalendar from '../components/reminder/ReminderCalendar';
import ReminderList from '../components/reminder/ReminderList';
import { getReminders } from '../services/firebase/reminderService';
import { toast } from 'react-toastify';
import { FaCalendarAlt, FaList, FaBirthdayCake, FaMedal, FaFilter } from 'react-icons/fa';
import './ReminderPage.css';

const ReminderPage = () => {
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('list');
    const [filterType, setFilterType] = useState('all');
    const [filterDays, setFilterDays] = useState(7);

    useEffect(() => {
        fetchReminders();
    }, [filterType, filterDays]);

    const fetchReminders = async () => {
        setLoading(true);
        try {
            const type = filterType === 'all' ? null : filterType;
            const response = await getReminders(filterDays, type);
            setReminders(response.data || []);
        } catch (error) {
            console.error('Error fetching reminders:', error);
            toast.error('Failed to fetch reminders');
        } finally {
            setLoading(false);
        }
    };

    const birthdayCount = reminders.filter(r => r.type === 'birthday').length;
    const anniversaryCount = reminders.filter(r => r.type === 'work_anniversary').length;
    const highPriorityCount = reminders.filter(r => r.daysUntil <= 2).length;

    return (
        <div className="d-flex" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
            <Sidebar />
            <div className="flex-grow-1" style={{ background: '#f4f6f9', minHeight: '100vh', minWidth: 0, maxWidth: '100vw' }}>
                <Navbar />
                <Container fluid className="p-2 p-sm-3 p-md-4" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
                    {/* Header */}
                    <div className="page-header mb-3 mb-md-4">
                        <div>
                            <h2 className="fw-bold text-dark mb-1 fs-3 fs-md-2">Reminders & Events</h2>
                            <p className="text-muted mb-0 small">
                                Track upcoming birthdays and work anniversaries
                            </p>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <Row className="g-2 g-md-3 mb-3 mb-md-4">
                        <Col xs={12} sm={4}>
                            <Card className="stat-card shadow-sm border-0 h-100">
                                <Card.Body className="d-flex align-items-center p-3">
                                    <div className="stat-icon birthday-icon me-3">
                                        <FaBirthdayCake size={22} />
                                    </div>
                                    <div>
                                        <h6 className="text-muted mb-1 small">Upcoming Birthdays</h6>
                                        <h3 className="fw-bold mb-0">{birthdayCount}</h3>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={12} sm={4}>
                            <Card className="stat-card shadow-sm border-0 h-100">
                                <Card.Body className="d-flex align-items-center p-3">
                                    <div className="stat-icon anniversary-icon me-3">
                                        <FaMedal size={22} />
                                    </div>
                                    <div>
                                        <h6 className="text-muted mb-1 small">Work Anniversaries</h6>
                                        <h3 className="fw-bold mb-0">{anniversaryCount}</h3>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={12} sm={4}>
                            <Card className="stat-card shadow-sm border-0 h-100">
                                <Card.Body className="d-flex align-items-center p-3">
                                    <div className="stat-icon priority-icon me-3">
                                        <FaFilter size={22} />
                                    </div>
                                    <div>
                                        <h6 className="text-muted mb-1 small">High Priority (≤2 days)</h6>
                                        <h3 className="fw-bold mb-0">{highPriorityCount}</h3>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Filters */}
                    <Card className="shadow-sm border-0 mb-3 mb-md-4 reminder-filter-card">
                        <Card.Body className="p-3">
                            <Row className="g-2 align-items-center">
                                <Col xs={12} md={7}>
                                    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                                        <label className="fw-semibold text-muted mb-0 small text-nowrap">Filter by Type:</label>
                                        <div className="reminder-type-buttons w-100">
                                            <Button
                                                variant={filterType === 'all' ? 'primary' : 'outline-primary'}
                                                onClick={() => setFilterType('all')}
                                                size="sm"
                                            >
                                                All
                                            </Button>
                                            <Button
                                                variant={filterType === 'birthday' ? 'info' : 'outline-info'}
                                                onClick={() => setFilterType('birthday')}
                                                size="sm"
                                            >
                                                <FaBirthdayCake className="me-1" /> Birthdays
                                            </Button>
                                            <Button
                                                variant={filterType === 'work_anniversary' ? 'warning' : 'outline-warning'}
                                                onClick={() => setFilterType('work_anniversary')}
                                                size="sm"
                                            >
                                                <FaMedal className="me-1" /> Anniversaries
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={12} md={5}>
                                    <div className="d-flex align-items-center justify-content-between justify-content-md-end gap-2 mt-2 mt-md-0">
                                        <label className="fw-semibold text-muted mb-0 small text-nowrap">Show Next:</label>
                                        <Form.Select
                                            value={filterDays}
                                            onChange={(e) => setFilterDays(parseInt(e.target.value))}
                                            style={{ minWidth: '110px', width: 'auto' }}
                                            size="sm"
                                        >
                                            <option value={7}>7 Days</option>
                                            <option value={14}>14 Days</option>
                                            <option value={30}>30 Days</option>
                                            <option value={60}>60 Days</option>
                                            <option value={90}>90 Days</option>
                                        </Form.Select>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tabbed View */}
                    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                        <Card className="shadow-sm border-0 overflow-hidden">
                            <Card.Header className="bg-white border-0 p-2 p-sm-3 pt-sm-3">
                                <Nav variant="tabs" className="custom-tabs">
                                    <Nav.Item>
                                        <Nav.Link eventKey="list" className="d-flex align-items-center justify-content-center gap-2">
                                            <FaList /> List View
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="calendar" className="d-flex align-items-center justify-content-center gap-2">
                                            <FaCalendarAlt /> Calendar View
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </Card.Header>
                            <Card.Body className="p-2 p-sm-4">
                                <Tab.Content>
                                    <Tab.Pane eventKey="list">
                                        <ReminderList reminders={reminders} loading={loading} />
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="calendar">
                                        <ReminderCalendar />
                                    </Tab.Pane>
                                </Tab.Content>
                            </Card.Body>
                        </Card>
                    </Tab.Container>
                </Container>
            </div>
        </div>
    );
};

export default ReminderPage;
